import { Edit } from '@mui/icons-material'
import { Box, styled, Typography } from '@mui/material'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useGetFormSchemasForMovement } from 'src/api/form-schema'
import { invalidateMainComponentCache, useBulkReleaseMainComponents } from 'src/api/main-component'
import { ReactComponent as LocalityIcon } from 'src/assets/icons/localityIcon.svg'
import { ReactComponent as ReleaseIcon } from 'src/assets/icons/releaseIcon.svg'
import { ReactComponent as SendToServiceStationIcon } from 'src/assets/icons/serviceStationIcon.svg'
import { AuthorizationProvider } from 'src/components/common/Authorization'
import { GridActionButton } from 'src/components/common/GridActionButton'
import BulkChangeLocationDialog from 'src/components/customer-admin/equipments/BulkChangeLocationDialog'
import { BulkEditEquipmentModal } from 'src/components/customer-admin/equipments/BulkEditEquipmentModal'
import { BulkSendToDialog } from 'src/components/customer-admin/equipments/BulkSendToDialog'
import BulkSendToLocalityDialog from 'src/components/customer-admin/equipments/BulkSendToLocalityDialog'
import { SendToLocalityDialog } from 'src/screens/serviceStations/SendToLocalityDialog'
import { CustomerCtx } from 'src/context/CustomerContext'
import { useCompleteChecklistsDialog } from 'src/hooks/useCompleteChecklistsDialog'
import { IMainComponentType, MainComponentStatus, MovementType } from 'src/enums'
import { AuthorizationType } from 'src/enums/auth'
import { permissionValue } from 'src/enums/permissionValue'
import { IFormSchemaReport } from 'src/generated-types/form-schema'
import { IMainComponent } from 'src/generated-types/main-component'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { useSendToLocalityDialogSubmit } from 'src/hooks/useSendToLocalityDialogSubmit'
import { MUIThemeColors } from 'src/mui-theme'
import { MainComponentSendModalComponent } from 'src/screens/localities/positions/components/MainComponentSendModal'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { invalidateDeviation } from 'src/api/deviation'

const permissionValues = [
  permissionValue.Locality.Mooring.Write,
  permissionValue.ServiceStation.Write,
  permissionValue.ClientAdminPermission,
  permissionValue.ClientAdminPortal.Equipment_Portal,
]

export interface BulkActionHeaderActionsProps {
  customerId: EntityId
  data: IMainComponent[]
  locality?: { id?: EntityId } | null
}

export const BulkActionHeaderActions: FC<BulkActionHeaderActionsProps> = ({
  customerId,
  data: selectedEquipments,
  locality,
}) => {
  const { t } = useTranslation(['main-component', 'common'])
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const showChecklistsDialog = useCompleteChecklistsDialog(customerId as EntityId)
  const { mutateAsync: bulkReleaseMainComponents } = useBulkReleaseMainComponents(customerId as EntityId)
  const isMultipleSelected = (selectedEquipments?.length ?? 0) > 0
  const [sideAnimationShown, setSideAnimationShown] = useState(false)

  const releaseEquipmentTypeIds = useMemo(
    () => Array.from(new Set(selectedEquipments.map(e => (e.typeId ?? e.type?.id) as number).filter(Boolean))),
    [selectedEquipments],
  )

  const { data: releaseSchemas, isLoading: isLoadingReleaseSchemas } = useGetFormSchemasForMovement(
    customerId as EntityId,
    { equipmentTypeIds: releaseEquipmentTypeIds, movementTypes: [MovementType.Release] },
  )

  useEffect(() => {
    if (isMultipleSelected) {
      const id = requestAnimationFrame(() => setSideAnimationShown(true))
      return () => cancelAnimationFrame(id)
    }
    setSideAnimationShown(false)
  }, [isMultipleSelected])

  const firstEquipment = selectedEquipments[0]

  const { onSubmit: submitSendToLocality, prepareEquipmentId } = useSendToLocalityDialogSubmit({
    customerId: customerId as EntityId,
    fallbackLocalityId: locality?.id,
    onSuccess: () => {
      invalidateMainComponentCache.invalidateAfterBulkAction(customerId as EntityId, selectedEquipments)
      invalidateDeviation.getDeviations(customerId)
    },
  })
  // Keep a ref updated every render so that after flushSync inside prepareEquipmentId
  // the latest submitSendToLocality (with the correct equipmentIds) is always used.
  const submitSendToLocalityRef = useRef(submitSendToLocality)
  submitSendToLocalityRef.current = submitSendToLocality
  const isMultipleLocalities = useMemo(() => {
    const localityIds = selectedEquipments
      .map(e => e.selectedLocalityId)
      .filter((id): id is number => id != null && id !== undefined)
    return new Set(localityIds).size > 1
  }, [selectedEquipments])

  const onBulkEdit = useCallback(() => {
    showDialog(
      BulkEditEquipmentModal,
      {
        componentProps: { selectedEquipments, customerId },
        opts: { maxWidth: 'md', fullWidth: true },
      },
      undefined,
      true,
    )
  }, [selectedEquipments, customerId, showDialog])

  const showBulkSendToLocalityDialog = useCallback(() => {
    if (!selectedEquipments?.length) return
    showDialog(
      BulkSendToLocalityDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          selectedEquipments,
          defaultLocalityId: locality?.id,
          onSuccess: () => {
            invalidateMainComponentCache.invalidateAfterBulkAction(customerId as EntityId, selectedEquipments)
          },
        },
        opts: { maxWidth: 'sm', fullWidth: true },
      },
      undefined,
      true,
    )
  }, [customerId, locality?.id, selectedEquipments, showDialog])

  const showSendToLocalityForSingleDialog = useCallback(() => {
    if (!firstEquipment) return
    prepareEquipmentId([firstEquipment.id] as EntityId[])
    showDialog(
      SendToLocalityDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          mainComponentId: firstEquipment.id as EntityId,
          mainComponentTypeId: (firstEquipment.typeId ?? firstEquipment.type?.id) as EntityId,
          onSubmit: (form: any, pop: () => void, formContext?: any) =>
            submitSendToLocalityRef.current(form, pop, formContext),
        },
        opts: { maxWidth: 'sm', fullWidth: true },
      },
      undefined,
      true,
    )
  }, [customerId, firstEquipment, prepareEquipmentId, showDialog])

  const showChangeLocationDialog = useCallback(
    async (_mainComponent: IMainComponent, localityId?: EntityId) => {
      await showDialog(
        BulkChangeLocationDialog,
        {
          componentProps: {
            customerId: customerId as EntityId,
            localityId: (localityId ?? selectedEquipments[0]?.selectedLocalityId ?? 0) as EntityId,
            selectedEquipments,
            isChangeCageHidden: isMultipleLocalities,
            closeMainDialog: () => {},
            onSuccess: () => {
              invalidateMainComponentCache.invalidateAfterBulkAction(customerId as EntityId, selectedEquipments)
            },
          },
          opts: { maxWidth: 'sm', fullWidth: true },
        },
        undefined,
        true,
      )
    },
    [customerId, isMultipleLocalities, selectedEquipments, showDialog],
  )

  const onRelease = useCallback(() => {
    const toRelease = selectedEquipments.filter(
      e => e.positionId != null && e.status === MainComponentStatus.Active && e.selectedLocalityId != null,
    )
    if (toRelease.length === 0) return

    const performRelease = async (reportsMap?: Map<number, IFormSchemaReport[]> | null, onDone?: () => void) => {
      await bulkReleaseMainComponents({
        items: toRelease.map(e => ({
          id: e.id as number,
          localityId: e.selectedLocalityId as number,
          formSchemaReports: reportsMap?.get(e.id as number) ?? [],
        })),
      })
      toast.success(t('main-component-released-from-cage-toast', { ns: 'main-component' }))
      invalidateMainComponentCache.invalidateAfterBulkRelease(
        customerId as EntityId,
        toRelease.map(e => ({
          id: e.id as EntityId,
          selectedLocalityId: e.selectedLocalityId as EntityId,
          positionId: e.positionId as EntityId,
          typeId: (e.typeId ?? e.type?.id) as EntityId,
        })),
      )
      invalidateDeviation.getDeviations(customerId)
      onDone?.()
    }

    showConfirmDialog(
      t('release-from-cage'),
      t('release-from-cage-description'),
      { acceptText: t('yes'), cancelText: t('no') },
      async closeConfirm => {
        closeConfirm()
        await showChecklistsDialog({
          isBulk: true,
          schemas: releaseSchemas,
          isLoadingSchemas: isLoadingReleaseSchemas,
          selectedEquipments: toRelease,
          confirmText: t('release', { ns: 'main-component' }),
          onConfirm: performRelease,
        })
      },
    )
  }, [
    selectedEquipments,
    customerId,
    bulkReleaseMainComponents,
    showConfirmDialog,
    showChecklistsDialog,
    releaseSchemas,
    isLoadingReleaseSchemas,
    t,
  ])

  const onSend = useCallback(() => {
    if (selectedEquipments.length > 1) {
      showDialog(
        BulkSendToDialog,
        {
          componentProps: {
            customerId: customerId as EntityId,
            selectedEquipments,
            onSuccess: () => {
              invalidateMainComponentCache.invalidateAfterBulkAction(customerId as EntityId, selectedEquipments)
            },
          },
          opts: { maxWidth: 'sm', fullWidth: true },
        },
        undefined,
        true,
      )
    } else {
      showDialog(
        MainComponentSendModalComponent,
        {
          title: t('sendTo', { ns: 'main-component' }),
          componentProps: {
            customerId: customerId as EntityId,
            localityId: locality?.id as EntityId,
            mooringId: firstEquipment?.position?.mooringId as EntityId,
            positionId: firstEquipment?.positionId as EntityId,
            id: firstEquipment?.id as EntityId,
            onlyServiceStation: false,
            onSuccess: () => {
              invalidateMainComponentCache.invalidateAfterBulkAction(customerId as EntityId, selectedEquipments)
            },
          },
          opts: { maxWidth: 'sm', fullWidth: true },
        },
        undefined,
      )
    }
  }, [
    customerId,
    locality?.id,
    firstEquipment,
    selectedEquipments,
    showDialog,
    t,
    prepareEquipmentId,
    submitSendToLocality,
  ])

  const actionsContent = (
    <Flex.Row justifyContent="center" alignItems="center" gap={1}>
      {!selectedEquipments?.some(mc => mc.positionId == null || mc.status != MainComponentStatus.Active) && (
        <GridActionButton
          onClick={onRelease}
          height="36px"
          width="36px"
          variant="outlined"
          shadowWeight={0}
          permissionBinaryValues={permissionValues}
          context={CustomerCtx}
          buttonIcon={<ReleaseIcon height="15px" width="15px" />}
          authorizationType={AuthorizationType.Disabled}
          toolTipTitle={t('release', { ns: 'main-component' })}
        />
      )}
      {!selectedEquipments?.some(
        m =>
          !(
            (m.selectedLocalityId && m?.status === MainComponentStatus.InLocalityStock) ||
            (m?.positionId && m?.status === MainComponentStatus.Active) ||
            (m.selectedLocalityId && m?.status === MainComponentStatus.Outgoing)
          ),
      ) &&
        !selectedEquipments?.some(
          m =>
            m.type?.componentType === IMainComponentType.Net &&
            !m.isValidCertificateAvailable &&
            m.status === MainComponentStatus.InLocalityStock,
        ) && (
          <GridActionButton
            permissionBinaryValues={permissionValues}
            context={CustomerCtx}
            authorizationType={AuthorizationType.Disabled}
            onClick={() =>
              selectedEquipments?.length && showChangeLocationDialog(selectedEquipments[0], locality?.id ?? undefined)
            }
            buttonIcon={<img style={{ height: '28px', width: '28px' }} src="/icons/change.svg" alt="change location" />}
            height="36px"
            width="36px"
            toolTipTitle={t('change-location', { ns: 'main-component' })}
          />
        )}
      {!selectedEquipments?.some(m => m?.status !== MainComponentStatus.NewlyRegistered) &&
        !selectedEquipments?.some(
          m => m?.type?.componentType === IMainComponentType.Net && !m?.isValidCertificateAvailable,
        ) && (
          <AuthorizationProvider permissions={permissionValues} Context={CustomerCtx} type={AuthorizationType.Disabled}>
            <GridActionButton
              onClick={
                selectedEquipments.length === 1 ? showSendToLocalityForSingleDialog : showBulkSendToLocalityDialog
              }
              variant="outlined"
              height="36px"
              sx={{ padding: '5px' }}
              width="36px"
              shadowWeight={0}
              buttonIcon={<LocalityIcon style={{ height: 28, width: 28 }} aria-hidden />}
              toolTipTitle={t('sendToLocality', { ns: 'main-component' })}
            />
          </AuthorizationProvider>
        )}
      {!selectedEquipments?.some(
        m =>
          !(
            (m.selectedLocalityId && m?.status === MainComponentStatus.InLocalityStock) ||
            (m?.positionId && m?.status === MainComponentStatus.Active) ||
            (m.selectedLocalityId && m?.status === MainComponentStatus.Outgoing) ||
            m?.status === MainComponentStatus.NewlyRegistered
          ),
      ) && (
        <AuthorizationProvider permissions={permissionValues} Context={CustomerCtx} type={AuthorizationType.Disabled}>
          <GridActionButton
            onClick={onSend}
            buttonIcon={<SendToServiceStationIcon style={{ height: 28, width: 28 }} aria-hidden />}
            height="36px"
            width="36px"
            toolTipTitle={t('sendTo', { ns: 'main-component' })}
          />
        </AuthorizationProvider>
      )}
      <AuthorizationProvider permissions={permissionValues} Context={CustomerCtx} type={AuthorizationType.Disabled}>
        <GridActionButton
          buttonIcon={<Edit />}
          onClick={onBulkEdit}
          variant="outlined"
          toolTipTitle={t('bulkEdit', { ns: 'common' })}
          height="36px"
          width="36px"
        />
      </AuthorizationProvider>
    </Flex.Row>
  )

  const showActions = (selectedEquipments?.length ?? 0) > 0
  if (!showActions) return null

  const bulkActionsLabel = t('selected', { ns: 'common' })

  if (isMultipleSelected) {
    return (
      <BulkActionsToolbarBox
        sx={{
          transform: sideAnimationShown ? 'translateX(0)' : 'translateX(100%)',
          opacity: sideAnimationShown ? 1 : 0,
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out',
        }}
      >
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} color="primary.dark">
            {bulkActionsLabel} <SelectedCountBadge>{selectedEquipments?.length}</SelectedCountBadge>:
          </Typography>
          {actionsContent}
        </Box>
      </BulkActionsToolbarBox>
    )
  }

  return <>{actionsContent}</>
}

const SelectedCountBadge = styled('span')(({ theme }) => ({
  backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain),
  color: '#fff',
  padding: '2px 8px',
  borderRadius: '20px',
  fontWeight: 600,
  fontSize: '0.75rem',
  marginRight: '6px',
}))

const BulkActionsToolbarBox = styled(Box)(({ theme }) => ({
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: '4px 10px',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${getThemeColor(theme, MUIThemeColors.primaryLight)}`,
  backgroundColor: getThemeColor(theme, MUIThemeColors.infoLight),
}))
